import { UpperCasePipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar';
import { ToastType } from './model/toast.model';

@Component({
  selector: 'siat-toast',
  imports: [MatSnackBarModule, MatIconModule, MatButton, UpperCasePipe],
  templateUrl: './snackbar.component.html',
  styleUrl: './snackbar.component.scss',
})
export class SnackbarComponent {
  /**
   * Reference to the snackbar.
   */
  snackBarRef = inject(MatSnackBarRef);

  /**
   * Toast type. A signal because `SnackbarService` sets these fields on the
   * component instance right after `openFromComponent()` creates it — a plain
   * property assignment on an already-attached dynamic component has no
   * notification of its own to schedule a change-detection pass, so without a
   * signal the snackbar can render with none of its content.
   */
  type = signal<ToastType | undefined>(undefined);

  /**
   * Enum for the toast type.
   */
  toastType = ToastType;

  /**
   * Toast header.
   */
  header = signal<string | undefined>(undefined);

  /**
   * Toast message.
   */
  message = signal<string | undefined>(undefined);

  /**
   * Toast class.
   */
  class = signal<string | undefined>(undefined);

  /**
   * Label for the custom action button (optional).
   */
  actionLabel = signal<string | undefined>(undefined);

  /**
   * Callback function for the custom action (optional).
   */
  actionCallback?: () => void;

  /**
   * Get the icon class based on the snackbar type.
   */
  getIcon(type: ToastType | undefined): string {
    switch (type) {
      case ToastType.SUCCESS:
        return 'check_box';
      case ToastType.DANGER:
        return 'error';
      case ToastType.INFO:
        return 'info';
      case ToastType.WARNING:
        return 'warning';
      default:
        return '';
    }
  }

  /**
   * Get the icon color class based on the snackbar type.
   */
  getIconColor(type: ToastType | undefined): string {
    switch (type) {
      case ToastType.SUCCESS:
        return 'text-success!';
      case ToastType.DANGER:
        return 'text-error!';
      case ToastType.INFO:
        return 'text-info!';
      case ToastType.WARNING:
        return 'text-warning!';
      case ToastType.ACTION:
        return 'text-info!';
      default:
        return '';
    }
  }

  /**
   * Get the default message based on the snackbar type.
   */
  getDefaultTitle(type: ToastType | undefined): string {
    switch (type) {
      case ToastType.SUCCESS:
        return 'GENERAL.default_success_title';
      case ToastType.DANGER:
        return 'GENERAL.default_danger_title';
      case ToastType.INFO:
        return 'GENERAL.default_info_title';
      case ToastType.WARNING:
        return 'GENERAL.default_danger_title';
      case ToastType.ACTION:
        return 'GENERAL.default_info_title';
      default:
        return '';
    }
  }

  /**
   * Execute the custom action.
   */
  onActionClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.actionCallback) {
      this.actionCallback();
    }

    this.snackBarRef.dismissWithAction();
  }
}
